import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Icon from '../../Img/Icon';
import { showPopupNew } from '../../../actions/popup-actions';
import { fetchAuthenticatedUser } from '../../../actions';
import { PopupTypes } from '../../../utils/constants';
import { PowerFunnelSurfaces, PowerFunnelGatedFeatures } from '../../Analytics/constants';

import styles from './PlayerBookmark.scss';

const BOOKMARK_OUTLINE_ICON = '/img/v3/icons/icon-24px-bookmark.svg';
const BOOKMARK_FILLED_ICON = '/img/v3/icons/icon-24px-bookmark-on.svg';
const ICON_SIZE = 24;
// Kept comfortably under UpgradeToPowerPopupV4's 5s ON_SUCCESS_TIMEOUT_MS window: that popup races
// onSuccess against its own 5s timeout without cancelling the loser, so an unbounded auth refresh
// could both swallow the payment confirmation and pop a late, contextless Save modal.
export const AUTH_REFRESH_TIMEOUT_MS = 3500;

const BookmarkStates = {
    FREE: 'free',
    LOADING: 'loading',
    OUTLINE: 'outline',
    FILLED: 'filled',
};

const PlayerBookmark = (props) => {
    const {
        playerId,
        playerName,
        isSaved,
        isLoading,
        savedListIds,
        onSaveSuccess,
        onRemoveSuccess,
        className,
        'data-testid': dataTestId,
    } = props;

    const dispatch = useDispatch();
    const user = useSelector(({ auth }) => auth?.user);
    const isMountedRef = useRef(true);
    useEffect(() => () => { isMountedRef.current = false; }, []);

    if (!user) {
        return null;
    }

    const isPowered = !!user.isPowered;

    const showSaveToListPopup = () => {
        dispatch(showPopupNew({
            popup: PopupTypes.SAVE_TO_LIST_POPUP,
            id: playerId,
            playerName,
            savedListIds,
            onSuccess: onSaveSuccess,
        }));
    };

    const showRemoveConfirmPopup = () => {
        dispatch(showPopupNew({
            popup: PopupTypes.REMOVE_PLAYER_CONFIRM,
            id: playerId,
            playerName,
            onSuccess: onRemoveSuccess,
        }));
    };

    const resolveBookmarkState = () => {
        if (!isPowered) {
            return BookmarkStates.FREE;
        }
        if (isLoading) {
            return BookmarkStates.LOADING;
        }
        return isSaved ? BookmarkStates.FILLED : BookmarkStates.OUTLINE;
    };

    const bookmarkState = resolveBookmarkState();
    const iconSrc = bookmarkState === BookmarkStates.FILLED ? BOOKMARK_FILLED_ICON : BOOKMARK_OUTLINE_ICON;

    const openSaveToListModal = async () => {
        // The checkout wrote a possibly-partial currentMember into the store-wide auth.user, so the
        // refresh must run even after an unmount (the header/nav read the same auth.user). It's
        // bounded by its own timeout so a slow auth call can't outlast the popup's 5s window.
        let refreshOk = false;
        try {
            await Promise.race([
                dispatch(fetchAuthenticatedUser()), // NOSONAR — Redux thunk returns a promise
                new Promise((_, reject) => { setTimeout(() => reject(new Error('auth refresh timed out')), AUTH_REFRESH_TIMEOUT_MS); }),
            ]);
            refreshOk = true;
        } catch {
            refreshOk = false;
        }
        if (isMountedRef.current && refreshOk) {
            showSaveToListPopup();
            return;
        }
        // Navigated away, or the refresh failed / timed out: surface the standard Power success popup
        // (the upsell suppresses its own while an onSuccess is set) so a member who paid always gets a
        // confirmation rather than a silent close or a late, contextless modal.
        dispatch(showPopupNew({ popup: PopupTypes.POWER_SUCCESS_POPUP_V2 }));
    };

    // The bookmark renders inside a row's profile <Link>: preventDefault stops the native <a> and
    // stopPropagation stops react-router's synthetic nav, so opening a modal never also navigates.
    const suppressAncestorNavigation = (e) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
    };

    const handleClick = (e) => {
        suppressAncestorNavigation(e);
        if (bookmarkState === BookmarkStates.LOADING) {
            return;
        }
        if (!isPowered) {
            dispatch(showPopupNew({
                popup: PopupTypes.UPGRADE_TO_POWER_POPUP,
                referringSurface: PowerFunnelSurfaces.PLAYER_PROFILE,
                gatedFeature: PowerFunnelGatedFeatures.SAVED_PLAYER_LIST,
                onSuccess: openSaveToListModal,
            }));
            return;
        }
        if (isSaved) {
            // Adding an already-saved player to a further list (the multi-list picker) is a later
            // story, so a filled bookmark has no path back into SaveToListModal yet.
            showRemoveConfirmPopup();
            return;
        }
        showSaveToListPopup();
    };

    const buttonClass = [
        styles.bookmark,
        bookmarkState === BookmarkStates.FREE ? styles.greyed : '',
        bookmarkState === BookmarkStates.LOADING ? styles.loading : '',
        className,
    ].filter(Boolean).join(' ');

    return (
        <button
            type="button"
            className={buttonClass}
            onClick={handleClick}
            aria-label={isSaved ? 'Remove from saved list' : 'Save to list'}
            aria-disabled={bookmarkState === BookmarkStates.LOADING || undefined}
            data-testid={dataTestId || undefined}
        >
            <span
                className={styles.iconWrapper}
                data-testid={dataTestId && `${dataTestId}.${bookmarkState}`}
            >
                <Icon src={iconSrc} size={ICON_SIZE} />
            </span>
        </button>
    );
};

PlayerBookmark.propTypes = {
    playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    playerName: PropTypes.string,
    isSaved: PropTypes.bool,
    isLoading: PropTypes.bool,
    savedListIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    onSaveSuccess: PropTypes.func,
    onRemoveSuccess: PropTypes.func,
    className: PropTypes.string,
    'data-testid': PropTypes.string,
};

PlayerBookmark.defaultProps = {
    playerName: null,
    isSaved: false,
    isLoading: false,
    savedListIds: null,
    onSaveSuccess: null,
    onRemoveSuccess: null,
    className: null,
    'data-testid': null,
};

export default PlayerBookmark;
